import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { RefreshCw } from 'lucide-react';
import MetricCard from './charts/MetricCard';
import DateRangePicker from './charts/DateRangePicker';
import SalesSplitChart from './charts/SalesSplitChart';
import RevenueSplitChart from './charts/RevenueSplitChart';
import ReportingSalesChart from './charts/ReportingSalesChart';
import OutfitCountChart from './charts/OutfitCountChart';
import TopCustomerCard from './charts/TopCustomerCard';
import { 
  DollarSign, 
  ShoppingCart, 
  CheckCircle, 
  UserPlus, 
  Users,
  TrendingUp
} from 'lucide-react';
import { 
  DashboardMetricsResponse, 
  ChartDataPoint, 
  DateRangeFilter 
} from '@/types/analytics';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const EnhancedDashboardView: React.FC = () => {
  // Enhanced Dashboard Analytics - Staging Deployment Test
  const { token } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Date range state
  const [dateRange, setDateRange] = useState<DateRangeFilter>(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30); // Default to last 30 days
    
    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
  });

  const fetchDashboardData = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await fetch(
        `${API_BASE}/api/tailors/analytics/metrics?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data: ${response.statusText}`);
      }

      const data: DashboardMetricsResponse = await response.json();
      setDashboardData(data);
    } catch (_err) {
      console.error('Error fetching dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboardData();
    }
  }, [token, dateRange]);

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({
      start_date: startDate,
      end_date: endDate
    });
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const convertToChartData = (data: any, type: 'sales' | 'revenue' | 'outfit'): ChartDataPoint[] => {
    if (!data) return [];
    
    return Object.entries(data).map(([key, value]) => ({
      name: key,
      value: typeof value === 'number' ? value : 0
    }));
  };

  const getMetricTrend = (current: number, previous: number): { trend: 'up' | 'down' | 'neutral', value: number } => {
    if (previous === 0) return { trend: 'neutral', value: 0 };
    
    const change = ((current - previous) / previous) * 100;
    if (Math.abs(change) < 1) return { trend: 'neutral', value: 0 };
    
    return {
      trend: change > 0 ? 'up' : 'down',
      value: Math.abs(change)
    };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-64"></div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-12"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to load dashboard</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Enhanced Dashboard</h2>
            <p className="text-gray-600 mt-1">Comprehensive business insights and performance metrics</p>
          </div>
          <div className="flex items-center space-x-3">
            <DateRangePicker
              startDate={dateRange.start_date}
              endDate={dateRange.end_date}
              onDateChange={handleDateRangeChange}
              loading={refreshing}
            />
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <MetricCard
          title="Amount Received"
          value={dashboardData?.metrics.amount_received || 0}
          icon={DollarSign}
          trend="neutral"
          loading={loading}
        />
        <MetricCard
          title="Total Sales"
          value={dashboardData?.metrics.total_sales || 0}
          icon={TrendingUp}
          trend="neutral"
          loading={loading}
        />
        <MetricCard
          title="Orders Received"
          value={dashboardData?.metrics.orders_received || 0}
          icon={ShoppingCart}
          trend="neutral"
          loading={loading}
        />
        <MetricCard
          title="Orders Closed"
          value={dashboardData?.metrics.orders_closed || 0}
          icon={CheckCircle}
          trend="neutral"
          loading={loading}
        />
        <MetricCard
          title="New Customers"
          value={dashboardData?.metrics.new_customers || 0}
          icon={UserPlus}
          trend="neutral"
          loading={loading}
        />
        <MetricCard
          title="Total Customers"
          value={dashboardData?.metrics.total_customers || 0}
          icon={Users}
          trend="neutral"
          loading={loading}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Split */}
        <SalesSplitChart
          data={convertToChartData(dashboardData?.sales_split, 'sales')}
          loading={loading}
          error={error}
        />

        {/* Revenue Split */}
        <RevenueSplitChart
          data={convertToChartData(dashboardData?.revenue_split, 'revenue')}
          loading={loading}
          error={error}
        />

        {/* Reporting Sales */}
        <ReportingSalesChart
          data={dashboardData?.weekly_sales || []}
          loading={loading}
          error={error}
        />

        {/* Outfit Count */}
        <OutfitCountChart
          data={convertToChartData(dashboardData?.outfit_count, 'outfit')}
          loading={loading}
          error={error}
        />
      </div>

      {/* Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopCustomerCard
          customers={dashboardData?.top_customers || []}
          loading={loading}
          error={error}
        />
        
        {/* Payment Mode Split - Placeholder for future implementation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Mode Split</h3>
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <p className="text-sm">Payment mode analytics</p>
              <p className="text-xs text-gray-400 mt-1">Coming soon</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboardView;
